import { createHttpEndpoint } from '../../utils'

import type {
  GetMerchantOrders,
  GetMerchantOrdersDetails,
  GetMerchantOrdersMetadata,
  GetSellerXpOrderDetails,
} from './merchant-orders-pages.types'

/**
 * Get the orders for a given merchant and set of filters.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/get-bm-merchants-orders}
 */
export const getMerchantOrders = createHttpEndpoint<GetMerchantOrders>({
  method: 'GET',
  operationId: 'get-bm-merchants-orders',
  path: '/bm/merchants/orders',
})

/**
 * Get one specific order for a given merchant.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/get-bm-merchants-orders-details}
 */
export const getMerchantOrdersDetails =
  createHttpEndpoint<GetMerchantOrdersDetails>({
    method: 'GET',
    operationId: 'patch-bm-merchants-orders',
    path: '/bm/merchants/orders/:orderId',
  })

/**
 * Updates a specific order for a given merchant.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/patch-bm-merchants-orders}
 */
export const patchMerchantOrdersDetails = createHttpEndpoint({
  method: 'PATCH',
  operationId: 'patch-bm-merchants-orders',
  path: '/bm/merchants/orders/:orderId',
})

/**
 * Get the orders metadata for the given seller.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/get-bm-merchants-orders-metadata}
 */
export const getMerchantOrdersMetadata =
  createHttpEndpoint<GetMerchantOrdersMetadata>({
    method: 'GET',
    operationId: 'get-bm-merchants-orders-metadata',
    path: '/bm/merchants/orders/metadata',
  })

/**
 * Export the orders delivery slips
 * Export the order IDs that have order lines without any IMEI number
 * Export the backship labels
 *
 * type exportType = 'backship-labels'| 'delivery-slips'| 'missing-imei-numbers'

 * @link {@see https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/post-bm-merchants-order-export-backship-labels}
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/post-bm-merchants-orders-export-delivery-slips}
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/post-bm-merchants-orders-export-missing-imei-numbers}

 */
export const postMerchantOrdersExport = createHttpEndpoint({
  method: 'POST',
  operationId: 'post-bm-merchants-orders-export',
  path: '/bm/merchants/orders/export/:exportType',
})

/**
 * Export the orders
 *
 * @link {@see https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/post-bm-merchants-orders-export}
 */
export const postMerchantOrdersExportAll = createHttpEndpoint({
  method: 'POST',
  operationId: 'post-bm-merchants-orders-export',
  path: '/bm/merchants/orders/export',
})

/**
 * Cancel an order line
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/put-bm-merchants-orderlines-id-cancel}
 */
export const putMerchantOrdersCancelOrderline = createHttpEndpoint({
  method: 'PUT',
  operationId: 'put-bm-merchants-orderlines-id-cancel',
  path: '/bm/merchants/orderlines/:orderLineId/cancel',
})

/**
 * Ship one specific order for a given merchant.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/put-bm-merchants-orders-ship}
 */
export const putMerchantOrdersShipOrder = createHttpEndpoint({
  method: 'PUT',
  operationId: 'put-bm-merchants-orders-ship',
  path: '/bm/merchants/orders/:orderId/ship',
})

/**
 * Validate an order line
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/put-bm-merchants-orderlines-id-validate}
 */
export const putMerchantOrdersValidateOrderline = createHttpEndpoint({
  method: 'PUT',
  operationId: 'put-bm-merchants-orderlines-id-validate',
  path: '/bm/merchants/orderlines/:orderLineId/validate',
})

/**
 * Partial update of a given orderline
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/patch-bm-merchants-orderlines-id}
 */
export const patchMerchantOrdersOrderline = createHttpEndpoint({
  method: 'PATCH',
  operationId: 'patch-bm-merchants-orderlines-id',
  path: '/bm/merchants/orderlines/:orderLineId',
})

/**
 * Import the tracking numbers or IMEIs in bulk using a csv
 * The importType can be either 'tracking-numbers' or 'imei-numbers'
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/post-bm-merchants-orders-import-imei-numbers}
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/post-bm-merchants-orders-import-tracking-numbers}
 */
export const postMerchantOrdersImport = createHttpEndpoint({
  method: 'POST',
  operationId: 'post-bm-merchants-orders-import',
  path: '/bm/merchants/orders/import/:importType',
})

/**
 * Validate a bunch of order line in bulk
 *
 * @link {@see https://devportal.backmarket.io/catalog/default/api/seller-orders-api/definition#tag/Merchant-order-page/operation/put-bm-merchants-orderlines-import-bulk-validate}
 */
export const bulkValidateSellerOrderLine = createHttpEndpoint({
  method: 'PUT',
  operationId: 'bulkValidateSellerOrderLine',
  path: '/bm/merchants/orderlines/bulk/validate',
})

export const requestNewPickup = createHttpEndpoint({
  method: 'POST',
  operationId: 'requestNewPickup',
  path: '/bm/orders/:orderId/new_pickup',
})

export const cancelOrderPickup = createHttpEndpoint({
  method: 'POST',
  operationId: 'cancelOrderPickup',
  path: '/bm/orders/cancel_pickup/:orderId',
})

export const getOrderDetails = createHttpEndpoint<GetSellerXpOrderDetails>({
  method: 'GET',
  operationId: 'getOrderDetails',
  path: '/api/seller-experience/orders/:orderId',
})

export const cancelOrderLine = createHttpEndpoint({
  method: 'POST',
  operationId: 'cancelOrderLine',
  path: '/api/seller-experience/orders/:orderId/items/cancel',
})
